import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, getTenantBySlug } from '../api';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const navigate = useNavigate();

  // Detect subdomain and fetch tenant info
  useEffect(() => {
    const getSubdomain = () => {
      const hostname = window.location.hostname;
      
      // Check if we're on a subdomain of datatriz.com
      if (hostname.endsWith('.datatriz.com')) {
        return hostname.replace('.datatriz.com', '');
      }
      
      // For local development or vercel preview, check for slug in localStorage or default
      // You can also use query params for testing: ?tenant=demo
      const params = new URLSearchParams(window.location.search);
      const tenantParam = params.get('tenant');
      if (tenantParam) {
        return tenantParam;
      }
      
      return null;
    };

    const fetchTenant = async () => {
      const slug = getSubdomain();
      
      if (slug) {
        try {
          const response = await getTenantBySlug(slug);
          setTenant(response.data);
        } catch (err) {
          console.error('Tenant not found:', err);
          setTenant(null);
        }
      }
      setTenantLoading(false);
    };

    fetchTenant();
  }, []);

  // Update page title when tenant changes
  useEffect(() => {
    if (tenant?.name) {
      document.title = tenant.name;
    } else {
      document.title = 'Valet Manager';
    }
  }, [tenant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Pass tenant slug if we're on a subdomain
      const response = await login(email, password, tenant?.slug || null);

      const { token, user } = response.data;

      // Save token to localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Call onLogin callback
      onLogin(user);

      // Redirect to home/dashboard
      navigate('/');
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError(err.response.data.message || 'Invalid email or password');
      } else {
        setError('Login failed. Please try again.');
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while fetching tenant
  if (tenantLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const displayName = tenant?.name || 'Valet Manager';

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>🚗 {displayName}</h1>
          <p style={styles.subtitle}>Management Portal</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="your@email.com"
              required
              autoFocus
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter your password"
              required
            />
          </div>

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            👤 Employee? <a href="/employee-portal" style={styles.link}>Go to Employee Portal</a>
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    padding: '20px'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    width: '100%',
    maxWidth: '400px'
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  logo: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1e3a5f',
    marginBottom: '8px'
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '16px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  },
  input: {
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  button: {
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#1e3a5f',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '8px'
  },
  error: {
    padding: '12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center'
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '14px',
    color: '#6b7280'
  },
  link: {
    color: '#1e3a5f',
    textDecoration: 'none',
    fontWeight: '600'
  }
};

export default Login;